import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import type { BulkCreateDocumentItem } from '@/api/documents';
import {
  bulkArchiveDocuments,
  bulkCreateDocuments,
  bulkRestoreDocuments,
  cancelDocumentAccessRequest,
  createQuestionnaireRequestDocuments,
  downloadDocuments,
  fetchArchivedDocuments,
  fetchDocumentPermissions,
  fetchDocuments,
  fetchNetworkDocuments,
  makeDocumentPublic,
  patchDocument,
  requestDocumentAccess,
  shareDocument,
} from '@/api/documents';
import { allDocumentTypesQueryOptions } from './useDocumentTypesQuery';

// ─── Params type (encoding belongs in the logic layer) ───────────────────────

export interface DocumentsParams {
  page: number;
  count: number;
  sort?: string;
  filter?: string;
}

/**
 * Which set of documents a `DocumentsTable` lists.
 * - `company`: the documents of the company being viewed (company profile tab).
 * - `network`: the viewer's own documents plus everything the network made
 *   visible to them (sidebar "Documents → My documents" page only).
 */
export type DocumentsScope = 'company' | 'network';

// ─── Query string builders ────────────────────────────────────────────────────

/**
 * Own-company profile → Documents tab (`scope='company'`, own company). No
 * `isWithNetworkDocuments` / `isNetworkShared` flag and no top-level
 * `companyId`: the API then runs its `company` mode, defaulting to the viewer's
 * company — exactly what the old frontend's `list-own` sends. The route param
 * can be a profile id rather than a company id, so it is deliberately not sent.
 * See DELTA-2547.
 */
function buildCompanyDocumentsQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/**
 * Sidebar "Documents → My documents" page (`scope='network'`): own documents
 * plus the network's (uploaded for others, linked public docs, approved shares).
 */
function buildOwnDocumentsQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('isWithNetworkDocuments', 'true');
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/** Another company's profile → Documents tab (`isOwnCompany=false`). */
function buildNetworkDocumentsQs(
  companyId: string,
  params: DocumentsParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  // Match the sidebar Documents tab: the viewer-centric query (getWithC2C)
  // scoped to the target company via the filter's company_id, so both views
  // return an identical set. Sending a top-level companyId without this flag
  // routes to the company-centric getList and diverges. See DELTA-2231.
  qs.set('isWithNetworkDocuments', 'true');
  const baseFilter = params.filter ? JSON.parse(params.filter) : {};
  qs.set('filter', JSON.stringify({ ...baseFilter, companyId }));
  if (params.sort) qs.set('sort', params.sort);
  return qs.toString();
}

/** Sidebar "Documents → Archived" page: the caller's own archived documents. */
function buildArchivedDocumentsQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/** Sidebar "Documents → Request access" page. */
function buildRequestAccessQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('isNetworkShared', 'false');
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

// ─── Query key factories ──────────────────────────────────────────────────────

// Every company-documents list key starts with
// `['network', 'company', companyId, 'documents']` so `invalidateDocs` and the
// create dialog reach all of them by prefix; the trailing segment keeps the
// lists from sharing a cache entry for the same companyId + params.
export const documentKeys = {
  companyList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'company'] as const,

  ownList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'own'] as const,

  networkList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'network'] as const,

  // Under the same prefix, so archiving from any list refreshes the archive.
  archivedList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'archived'] as const,

  requestAccess: (params: DocumentsParams) =>
    ['documents', 'request-access', params] as const,
};

// ─── queryOptions factories (usable in route loaders) ────────────────────────

export function companyDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.companyList(companyId, params),
    queryFn: () => fetchDocuments(buildCompanyDocumentsQs(params)),
    staleTime: 60_000,
  });
}

export function ownDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.ownList(companyId, params),
    queryFn: () => fetchDocuments(buildOwnDocumentsQs(params)),
    staleTime: 60_000,
  });
}

export function networkDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.networkList(companyId, params),
    queryFn: () => fetchDocuments(buildNetworkDocumentsQs(companyId, params)),
    staleTime: 60_000,
  });
}

export function archivedDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.archivedList(companyId, params),
    queryFn: () => fetchArchivedDocuments(buildArchivedDocumentsQs(params)),
    staleTime: 60_000,
  });
}

export function requestAccessQueryOptions(params: DocumentsParams) {
  return queryOptions({
    queryKey: documentKeys.requestAccess(params),
    queryFn: () => fetchNetworkDocuments(buildRequestAccessQs(params)),
    staleTime: 60_000,
  });
}

export function documentTypesQueryOptions() {
  return allDocumentTypesQueryOptions({ documentClass: 'COMPANY' });
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Picks the list for the surface:
 * - another company (`isOwnCompany=false`) → its documents as the viewer sees
 *   them (`scope` makes no difference there);
 * - own company + `scope='company'` → own company profile tab;
 * - own company + `scope='network'` → My documents page.
 *
 * One `useQuery` per list with `enabled` (the options carry different key
 * tuples, so they cannot share one typed `useQuery` call); only the selected
 * one fetches.
 */
export function useDocumentsQuery(
  companyId: string,
  params: DocumentsParams,
  isOwnCompany: boolean,
  scope: DocumentsScope,
) {
  const isCompanyList = isOwnCompany && scope === 'company';
  const isOwnList = isOwnCompany && scope === 'network';
  const companyQuery = useQuery({
    ...companyDocumentsQueryOptions(companyId, params),
    enabled: isCompanyList,
  });
  const ownQuery = useQuery({
    ...ownDocumentsQueryOptions(companyId, params),
    enabled: isOwnList,
  });
  const networkQuery = useQuery({
    ...networkDocumentsQueryOptions(companyId, params),
    enabled: !isOwnCompany,
  });
  if (isCompanyList) return companyQuery;
  if (isOwnList) return ownQuery;
  return networkQuery;
}

export function useArchivedDocuments(
  companyId: string,
  params: DocumentsParams,
) {
  return useQuery({
    ...archivedDocumentsQueryOptions(companyId, params),
    enabled: !!companyId,
  });
}

export function useRequestAccessDocuments(params: DocumentsParams) {
  return useQuery(requestAccessQueryOptions(params));
}

export function useDocumentTypes() {
  return useQuery(documentTypesQueryOptions());
}

export function useDocumentPermissions(id: string | null) {
  return useQuery({
    queryKey: ['document-permissions', id],
    queryFn: () => fetchDocumentPermissions(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function usePatchDocument() {
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; documentTypeId?: string };
    }) => patchDocument(id, body),
  });
}

export function useBulkArchiveDocuments() {
  return useMutation({
    mutationFn: (ids: string[]) => bulkArchiveDocuments(ids),
  });
}

export function useBulkRestoreDocuments() {
  return useMutation({
    mutationFn: (ids: string[]) => bulkRestoreDocuments(ids),
  });
}

export function useBulkCreateDocuments() {
  return useMutation({
    mutationFn: (payload: {
      companyId: string;
      documents: BulkCreateDocumentItem[];
    }) => bulkCreateDocuments(payload),
  });
}

/**
 * Creates documents through a questionnaire answer request rather than by naming
 * a company — the only path that works when answering on another company's
 * behalf. See `createQuestionnaireRequestDocuments`.
 */
export function useCreateQuestionnaireRequestDocuments() {
  return useMutation({
    mutationFn: (payload: {
      requestId: string;
      documents: Array<{
        name: string;
        documentTypeId: string;
        fileId: string;
      }>;
    }) =>
      createQuestionnaireRequestDocuments(payload.requestId, {
        documents: payload.documents,
      }),
  });
}

export function useDownloadDocuments() {
  return useMutation({
    mutationFn: (ids: string[]) => downloadDocuments(ids),
  });
}

export function useShareDocument() {
  return useMutation({
    mutationFn: ({ id, companyIds }: { id: string; companyIds: string[] }) =>
      shareDocument(id, companyIds),
  });
}

export function useMakeDocumentPublic() {
  return useMutation({
    mutationFn: (id: string) => makeDocumentPublic(id),
  });
}

export function useRequestDocumentAccess() {
  return useMutation({
    mutationFn: (id: string) => requestDocumentAccess(id),
  });
}

export function useCancelDocumentAccessRequest() {
  return useMutation({
    mutationFn: (id: string) => cancelDocumentAccessRequest(id),
  });
}
